/**
 * 🔒 SICHERE DATEI-VALIDIERUNG
 * 
 * Umfassende Validierung von Dateien gegen Malware und unerlaubte Dateitypen.
 * Prüft sowohl MIME-Types als auch Datei-Signaturen (Magic Numbers).
 */

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  rejectedFiles?: File[];
}

// 🔒 ERLAUBTE DATEITYPEN - NUR PDF, DOCX, TXT, ZIP
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.zip'] as const;

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed'
] as const;

// 🔒 DATEI-SIGNATUREN (Magic Numbers) für echte Typ-Validierung
const FILE_SIGNATURES: Record<string, number[][]> = {
  pdf: [[0x25, 0x50, 0x44, 0x46]], // %PDF
  docx: [[0x50, 0x4B, 0x03, 0x04]], // ZIP header (DOCX ist ZIP-basiert)
  zip: [
    [0x50, 0x4B, 0x03, 0x04], // Standard ZIP
    [0x50, 0x4B, 0x05, 0x06], // Empty ZIP
    [0x50, 0x4B, 0x07, 0x08]  // ZIP with different header
  ],
  txt: [] // TXT hat keine eindeutige Signatur, verwende MIME-Type
};

// 🚨 GEFÄHRLICHE DATEITYPEN - BLACKLIST
const DANGEROUS_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.com', '.scr', '.pif', '.vbs', '.js', '.jar',
  '.app', '.deb', '.pkg', '.dmg', '.msi', '.run', '.bin', '.sh',
  '.ps1', '.psm1', '.vb', '.vbe', '.ws', '.wsf', '.wsh', '.hta',
  '.cpl', '.msc', '.inf', '.reg', '.url', '.lnk', '.asp', '.php',
  '.jsp', '.py', '.rb', '.pl', '.go', '.rs', '.c', '.cpp', '.cs'
] as const;

/**
 * Liest die ersten Bytes einer Datei zur Signatur-Validierung
 */
async function readFileSignature(file: File, bytesToRead: number = 8): Promise<number[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      const uint8Array = new Uint8Array(arrayBuffer);
      resolve(Array.from(uint8Array));
    };
    
    reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
    
    // Nur die ersten Bytes lesen
    const blob = file.slice(0, bytesToRead);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Prüft ob eine Datei-Signatur mit den erlaubten Typen übereinstimmt
 */
function validateFileSignature(signature: number[], expectedSignatures: number[][]): boolean {
  if (expectedSignatures.length === 0) return true; // Für TXT (keine eindeutige Signatur)
  
  return expectedSignatures.some(expected => {
    return expected.every((byte, index) => signature[index] === byte);
  });
}

/**
 * Extrahiert die Datei-Extension (mit Punkt)
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? '' : filename.substring(lastDot).toLowerCase();
}

/**
 * Bestimmt den erwarteten Dateityp basierend auf Extension
 */
function getExpectedFileType(extension: string): keyof typeof FILE_SIGNATURES | null {
  switch (extension.toLowerCase()) {
    case '.pdf': return 'pdf';
    case '.docx': return 'docx';
    case '.txt': return 'txt';
    case '.zip': return 'zip';
    default: return null;
  }
}

/**
 * 🔒 HAUPTVALIDIERUNGSFUNKTION
 * 
 * Validiert eine einzelne Datei gegen alle Sicherheitskriterien:
 * 1. Extension-Check (Whitelist)
 * 2. MIME-Type-Validierung
 * 3. Datei-Signatur-Prüfung (Magic Numbers)
 * 4. Gefährliche Extensions (Blacklist)
 * 5. Dateigröße-Limits
 */
export async function validateFile(
  file: File, 
  maxSizeBytes: number = 10 * 1024 * 1024, // 10MB Standard
  allowedExtensions: readonly string[] = ALLOWED_EXTENSIONS
): Promise<FileValidationResult> {
  try {
    const extension = getFileExtension(file.name);
    
    // 🚨 1. BLACKLIST-CHECK: Gefährliche Dateitypen
    if (DANGEROUS_EXTENSIONS.includes(extension as any)) {
      return {
        isValid: false,
        error: `Sicherheitsrisiko: Dateityp "${extension}" ist nicht erlaubt. Nur PDF, DOCX, TXT und ZIP sind zulässig.`,
        rejectedFiles: [file]
      };
    }
    
    // 🔒 2. WHITELIST-CHECK: Erlaubte Extensions
    if (!allowedExtensions.includes(extension as any)) {
      return {
        isValid: false,
        error: `Dateityp "${extension}" nicht erlaubt. Nur ${allowedExtensions.join(', ')} sind zulässig.`,
        rejectedFiles: [file]
      };
    }
    
    // 📏 3. DATEIGRÖSSE-CHECK
    if (file.size > maxSizeBytes) {
      const maxSizeMB = Math.round(maxSizeBytes / 1024 / 1024);
      const fileSizeMB = Math.round(file.size / 1024 / 1024 * 100) / 100;
      return {
        isValid: false,
        error: `Datei zu groß: ${fileSizeMB}MB. Maximum: ${maxSizeMB}MB`,
        rejectedFiles: [file]
      };
    }
    
    // 🔒 4. MIME-TYPE-CHECK
    if (!ALLOWED_MIME_TYPES.includes(file.type as any)) {
      return {
        isValid: false,
        error: `MIME-Type "${file.type}" nicht erlaubt. Datei könnte manipuliert sein.`,
        rejectedFiles: [file]
      };
    }
    
    // 🔒 5. DATEI-SIGNATUR-CHECK (Magic Numbers)
    const expectedType = getExpectedFileType(extension);
    if (expectedType && FILE_SIGNATURES[expectedType]) {
      try {
        const signature = await readFileSignature(file);
        const expectedSignatures = FILE_SIGNATURES[expectedType];
        
        if (!validateFileSignature(signature, expectedSignatures)) {
          return {
            isValid: false,
            error: `Datei-Inhalt stimmt nicht mit Extension überein. Möglicherweise umbenannte oder manipulierte Datei.`,
            rejectedFiles: [file]
          };
        }
      } catch (signatureError) {
        return {
          isValid: false,
          error: `Fehler beim Validieren der Datei-Signatur.`,
          rejectedFiles: [file]
        };
      }
    }
    
    // ✅ Alle Checks bestanden
    return { isValid: true };
    
  } catch (error) {
    return {
      isValid: false,
      error: `Unerwarteter Fehler bei Datei-Validierung: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
      rejectedFiles: [file]
    };
  }
}

/**
 * 🔒 BATCH-VALIDIERUNG
 * 
 * Validiert mehrere Dateien gleichzeitig
 */
export async function validateFiles(
  files: File[], 
  maxSizeBytes: number = 10 * 1024 * 1024,
  allowedExtensions: readonly string[] = ALLOWED_EXTENSIONS
): Promise<FileValidationResult> {
  const rejectedFiles: File[] = [];
  const validFiles: File[] = [];
  const errors: string[] = [];
  
  for (const file of files) {
    const result = await validateFile(file, maxSizeBytes, allowedExtensions);
    
    if (result.isValid) {
      validFiles.push(file);
    } else {
      rejectedFiles.push(file);
      if (result.error) {
        errors.push(`${file.name}: ${result.error}`);
      }
    }
  }
  
  if (rejectedFiles.length > 0) {
    return {
      isValid: false,
      error: errors.join('\n'),
      rejectedFiles
    };
  }
  
  return { isValid: true };
}

/**
 * 🎯 SPEZIELLE VALIDIERUNGEN für verschiedene Upload-Bereiche
 */

// Einzelne Dokumente (PDF, DOCX, TXT)
export async function validateDocuments(files: File[]): Promise<FileValidationResult> {
  return validateFiles(files, 10 * 1024 * 1024, ['.pdf', '.docx', '.txt']);
}

// ZIP-Dateien für Bulk-Upload
export async function validateZipFile(file: File): Promise<FileValidationResult> {
  return validateFile(file, 50 * 1024 * 1024, ['.zip']); // 50MB für ZIP
}

// Hauptupload (nur ZIP)
export async function validateMainUpload(file: File): Promise<FileValidationResult> {
  return validateFile(file, 100 * 1024 * 1024, ['.zip']); // 100MB für Hauptupload
} 